import { useQuery } from '@apollo/client';
import { GET_FREE_PLAN_LIMITS } from '../graphql/queries';

export const usePlanLimits = (catalogId) => {
    const { data, loading, error } = useQuery(GET_FREE_PLAN_LIMITS, {
        variables: { catalogId },
        skip: !catalogId,
        fetchPolicy: 'cache-and-network'
    });

    const limits = data?.getFreePlanLimits;


    // Add platform fee info
    const platformFee = limits?.platformFeePercentage || 1.5;
    const paystackFee = 1.5; // Constant
    const totalFee = platformFee + paystackFee;
    const vendorShare = 100 - totalFee;

    const checkCanAdd = (type) => {
        if (!limits) return { canAdd: true, message: null };

        switch (type) {
            case 'product':
                return {
                    canAdd: limits.products.canAdd,
                    current: limits.products.current,
                    limit: limits.products.limit,
                    message: !limits.products.canAdd
                        ? `You've reached the free plan limit of ${limits.products.limit} products per store. More features coming soon!`
                        : null
                };
            case 'category':
                return {
                    canAdd: limits.categories.canAdd,
                    current: limits.categories.current,
                    limit: limits.categories.limit,
                    message: !limits.categories.canAdd
                        ? `You've reached the free plan limit of ${limits.categories.limit} categories per store. More features coming soon!`
                        : null
                };
            case 'customer':
                return {
                    canAdd: limits.customers.canAdd,
                    current: limits.customers.current,
                    limit: limits.customers.limit,
                    message: !limits.customers.canAdd
                        ? `You've reached the free plan limit of ${limits.customers.limit} customers per store. More features coming soon!`
                        : null
                };
            default:
                return { canAdd: true, message: null };
        }
    };

    // Digital product specific checks
    const checkDigitalFileSize = (fileSize) => {
        if (!limits) return { canAdd: true, message: null };

        const maxSize = limits.digitalFiles?.maxFileSize || 100 * 1024 * 1024;
        const canAdd = fileSize <= maxSize;

        return {
            canAdd,
            fileSize,
            limit: maxSize,
            limitMB: maxSize / (1024 * 1024),
            message: !canAdd
                ? `File size exceeds ${maxSize / (1024 * 1024)}MB limit.`
                : null
        };
    };

    const checkDigitalFileCount = (currentCount, newCount = 1) => {
        if (!limits) return { canAdd: true, message: null };

        const maxFiles = limits.digitalFiles?.maxFilesPerProduct || 5;
        const totalFiles = currentCount + newCount;
        const canAdd = totalFiles <= maxFiles;

        return {
            canAdd,
            current: currentCount,
            limit: maxFiles,
            message: !canAdd
                ? `You can only add ${maxFiles} digital files per product on the free plan.`
                : null
        };
    };

    const checkTotalDigitalSize = (currentTotal, newFileSize, existingFileSizes = []) => {
        if (!limits) return { canAdd: true, message: null };

        const maxTotal = limits.digitalFiles?.maxTotalSizePerProduct || 500 * 1024 * 1024;
        const currentTotalSize = existingFileSizes.reduce((sum, size) => sum + size, currentTotal);
        const totalSize = currentTotalSize + newFileSize;
        const canAdd = totalSize <= maxTotal;

        return {
            canAdd,
            currentSize: currentTotalSize,
            totalSize,
            limit: maxTotal,
            limitMB: maxTotal / (1024 * 1024),
            message: !canAdd
                ? `Total digital content size cannot exceed ${maxTotal / (1024 * 1024)}MB per product.`
                : null
        };
    };

    return {
        limits,
        loading,
        error,
        platformFee,
        paystackFee,
        totalFee,
        vendorShare,
        checkCanAdd,
        checkDigitalFileSize,
        checkDigitalFileCount,
        checkTotalDigitalSize
    };
};